import { useState } from 'react';

import { Button } from '@components/button';
import { FormLabelContainer } from '@components/form-label/form-label';
import { IconFormIndicatorInfoCurrentColor } from '@components/icons/form-indicator-info-current-color';
import { InfoBox } from '@components/info-box';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateJourneyForm } from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';

import { getSlugSaveError } from './get-slug-save-error';
import { isValidSlug } from './is-valid-slug';

const SLUG_FIELD_ID = 'share-form-slug';

export type ShareFormSlugEditorProps = {
  form: JourneyForm;
  onSaved: (form: JourneyForm) => void;
};

export const ShareFormSlugEditor = ({ form, onSaved }: ShareFormSlugEditorProps) => {
  const { t } = useScopedTranslate(locale);
  const updateMutation = useUpdateJourneyForm(form.shortId);

  const [slugDraft, setSlugDraft] = useState(form.slug);
  const [slugError, setSlugError] = useState<string | undefined>(undefined);

  const hasSavedSlug = Boolean(form.slug);
  const trimmedSlugDraft = slugDraft.trim();
  const isDirty = trimmedSlugDraft !== form.slug;
  const isDraftAcceptable = trimmedSlugDraft === '' || isValidSlug(trimmedSlugDraft);
  const canSave = isDirty && isDraftAcceptable && !updateMutation.isLoading;
  const draftError =
    trimmedSlugDraft && !isValidSlug(trimmedSlugDraft) ? t('invalidSlug') : undefined;

  const handleSlugChange = (value: string) => {
    if (value !== slugDraft) {
      setSlugError(undefined);
    }
    setSlugDraft(value);
  };

  const handleCancelSlug = () => {
    setSlugDraft(form.slug);
    setSlugError(undefined);
  };

  const saveSlugOnEnter = () => {
    if (canSave) {
      handleSaveSlug();
    }
  };

  const handleSaveSlug = async () => {
    const submittedSlug = trimmedSlugDraft;
    setSlugDraft(submittedSlug);
    try {
      const updated = await updateMutation.mutateAsync({ slug: submittedSlug });
      onSaved(updated);
      setSlugError(undefined);
      setSlugDraft((draft) => {
        return draft === submittedSlug ? updated.slug : draft;
      });
    } catch (error) {
      const message = getSlugSaveError(error, t('saveError'));
      if (message) {
        setSlugError(message);
      }
    }
  };

  return (
    <>
      <Stack space={4}>
        <FormLabelContainer id={SLUG_FIELD_ID} label={t('slugLabel')}>
          <Stack orientation="horizontal" space={2} align="flex-start">
            <TextField
              id={SLUG_FIELD_ID}
              label=""
              value={slugDraft}
              placeholder={t('slugPlaceholder')}
              onChange={handleSlugChange}
              onEnter={saveSlugOnEnter}
              error={slugError ?? draftError}
              isErrorAnnounced
              maxChars={255}
              isFullWidth
            />
            <Button
              text={t('save')}
              variant="primary-blue"
              isDisabled={!canSave}
              onClick={handleSaveSlug}
            />
            {isDirty && (
              <Button
                text={t('core.cancel')}
                variant="secondary"
                onClick={handleCancelSlug}
                isDisabled={updateMutation.isLoading}
              />
            )}
          </Stack>
        </FormLabelContainer>
        {hasSavedSlug && (
          <InfoBox
            variant="indigo"
            icon={<IconFormIndicatorInfoCurrentColor />}
            text={t('slugWarning')}
          />
        )}
      </Stack>
      <Separator />
    </>
  );
};

const locale = {
  en: {
    slugLabel: 'URL Slug',
    slugPlaceholder: 'Enter a slug...',
    slugWarning: 'If the URL Slug is edited, links already shared will no longer work',
    invalidSlug: 'The URL Slug can only contain letters, numbers, hyphens and underscores.',
    save: 'Save',
    saveError: 'Could not save the URL Slug. Please try again.',
  },
};
